import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ObservableHelper } from '../helpers/observable-helper';
import { User } from '../model/user';
import { SubscriptionBasedComponent } from '../subscription-helpers/subscription-based.component';
import { ClientCompanySelectService } from './client-company-select.service';
import { RequestService } from './request.service';
import { SnackService } from './snack.service';
import { TabsService } from './tabs.service';
import { UserSubmit } from '../pages/users/edit-employee-user/helper/user-submit';
import { _post } from './base.service';
import { LoadingService } from './loading.service';
import { StringHelper } from '../helpers/string-helper';
import * as globals from '../../globals/globals';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends SubscriptionBasedComponent {
  private static BASE_URL = '/auth';
  private static AUTH_STATE_KEY = 'auth-state';
  private readonly post: <T>(
    path: string,
    body: UserSubmit,
    deserialize: (response: any) => T
  ) => Observable<T>;

  public loginevent = new Subject<User>();
  public recoveryEvent = new Subject();
  public resetPasswordEvent = new Subject();
  public authWithTokenEvent = new Subject();
  public getAndSetEvent = new Subject<User>();
  public impersonationChange = new Subject();
  private user: User;
  private shortLivedToken: string;

  private authenticatedUserSubscribable = new BehaviorSubject<User>(null);

  constructor(
    http: HttpClient,
    public jwtHelper: JwtHelperService,
    private snackService: SnackService,
    private requestService: RequestService,
    loadingService: LoadingService,
    private router: Router,
    private tabsService: TabsService,
    private clientCompanySelectService: ClientCompanySelectService,
    private activatedRoute: ActivatedRoute
  ) {
    super();
    const requestServices = {
      http,
      loadingService,
      router,
      snackService,
    };
    this.post = _post(AuthService.BASE_URL, requestServices);

    this.isAuthenticated() &&
      this.getCurrentUser()
        .toPromise()
        .then((res) => {
          this.user = res.body;
          this.authenticatedUserSubscribable.next(this.user);
        });

    this.addSubscription(this.activatedRoute.queryParams, (params) => {
      this.shortLivedToken = params['token'];
    });
  }

  public setAuthenticatedStateFromToken(accessToken: string, user: User) {
    localStorage.setItem('inpress_access_token', accessToken);
    this.user = user;
    this.authenticatedUserSubscribable.next(this.user);
    this.loginevent.next(user);
  }

  public login(username: string, password: string): Promise<boolean> {
    const params = {
      username: username,
      password: password,
    };

    return this.post('/login', params, (res) => res)
      .toPromise()
      .then((res) => {
        const data: any = res;
        if (data.accessToken) {
          this.setAuthenticatedStateFromToken(data.accessToken, data.user);
          return true;
        }
      })
      .catch((error) => {
        if (error.status === 400 || error.status === 401) {
          this.snackService.showError(
            'Je e-mail of wachtwoord is niet correct. Probeer opnieuw aub.'
          );
        }
        return false;
      });
  }

  public loginWithShortLivedToken(): Promise<Boolean> {
    if (StringHelper.isEmptyOrWhiteSpace(this.shortLivedToken))
      return Promise.resolve(false);

    const params = {
      token: this.shortLivedToken,
    };

    return this.requestService
      .post(params, '/auth/login/token')
      .toPromise()
      .then((res) => {
        const data: any = res;
        if (!data.accessToken) {
          this.snackService.showError(data.message);
          return false;
        }

        localStorage.setItem('inpress_access_token', data.accessToken);
        this.user = data.user;
        this.authenticatedUserSubscribable.next(this.user);
        this.loginevent.next(this.user);
        return true;
      });
  }

  public requestAccountRecovery(email: string) {
    const params = {
      email: email,
    };

    return this.addSubscription(
      this.requestService.post(params, '/auth/recoverAccount'),
      (res) => {
        this.recoveryEvent.next(res.success);
      }
    );
  }

  public authenticateWithToken(token: string) {
    let params = [{ key: 'token', value: encodeURIComponent(token) }];
    return this.addSubscription(
      this.requestService.get('/auth/recover', params),
      (res) => {
        const data: any = res;
        if (!data.body.accessToken) {
          this.snackService.showError(data.body.message);
          this.authWithTokenEvent.next(false);
          return;
        }
        localStorage.setItem('inpress_access_token', data.body.accessToken);
        this.user = data.body.user;
        this.authWithTokenEvent.next(true);
      }
    );
  }

  public resetPassword(password: string) {
    return this.addSubscription(
      this.requestService.post({ password: password }, '/users/changePassword'),
      (res) => {
        this.resetPasswordEvent.next(res.success);
        this.authenticatedUserSubscribable.next(this.user);
      }
    );
  }

  public logout() {
    const decodedToken = this.jwtHelper.decodeToken<Record<string, unknown>>(
      this.getToken()
    );

    if (decodedToken && decodedToken.employee === true) {
      sessionStorage.setItem('shouldUseLegacyLogin', 'true');
    } else {
      sessionStorage.removeItem('shouldUseLegacyLogin');
    }

    localStorage.removeItem('inpress_access_token');
    this.user = null;
    this.tabsService.clearTabContent();
  }

  public getToken(): string {
    return localStorage.getItem('inpress_access_token');
  }

  public getAuthenticatedUser(): User {
    return this.user;
  }

  public getAuthenticatedUserSubscribable(): Subject<User> {
    return this.authenticatedUserSubscribable;
  }

  public waitUntilAuthenticatedUserLoadedSubscribable(): Observable<User> {
    return ObservableHelper.untilFirstNonNull(
      this.authenticatedUserSubscribable
    );
  }

  public isAuthenticated(): boolean {
    // get the token
    // const token = this.getToken();
    // return a boolean reflecting
    // whether or not the token is expired
    return this.getToken() !== null && !this.jwtHelper.isTokenExpired();
  }

  public shouldUseLegacyLogin(): boolean {
    return sessionStorage.getItem('shouldUseLegacyLogin') === 'true';
  }

  public getCurrentUser(): Observable<any> {
    return this.requestService.get('/auth/session-user');
  }

  public getAndSetCurrentUser(): Promise<void> {
    return this.getCurrentUser()
      .toPromise()
      .then((u) => {
        this.user = u.body;
        this.getAndSetEvent.next(this.user);
      });
  }

  public impersonateUser(user: User, hasCompanies = true): void {
    this.addSubscription(
      this.requestService.getNormal('/auth/impersonate/' + user.id),
      (data) => {
        if (data.accessToken) {
          localStorage.setItem('inpress_access_token', data.accessToken);
          this.user = data.user;
          this.tabsService.clearTabs();
          this.clientCompanySelectService.resetSelectableCompanies(
            hasCompanies
          );
          this.impersonationChange.next();
          this.authenticatedUserSubscribable.next(this.user);
        }
        if (this.user && this.user.hasNewPortalAccess) {
          window.location.href = globals.newPortalUrl;
        } else {
          this.router.navigateByUrl('/');
        }
      }
    );
  }

  public stopImpersonate() {
    //redirectUrl can be modified to e.g. custom url for each user role
    const redirectUrl = '/';
    this.addSubscription(
      this.requestService.getNormal('/auth/stop-impersonate/'),
      (data) => {
        if (data.accessToken) {
          localStorage.setItem('inpress_access_token', data.accessToken);
          this.user = data.user;
          this.tabsService.clearTabs();
          this.clientCompanySelectService.resetSelectableCompanies();
          this.impersonationChange.next();
          this.authenticatedUserSubscribable.next(this.user);
        }
        this.router.navigateByUrl(redirectUrl);
      }
    );
  }

  isImpersonating(): boolean {
    const token = this.getToken();
    return this.jwtHelper.decodeToken(token).oldId != null;
  }

  generateAndStoreSessionState(): string {
    const state = crypto.randomUUID();
    sessionStorage.setItem(AuthService.AUTH_STATE_KEY, state);
    return state;
  }

  verifySessionState(returnedState: string): boolean {
    const storedState = sessionStorage.getItem(AuthService.AUTH_STATE_KEY);
    console.log('storedState', storedState);
    const valid = storedState === returnedState;

    sessionStorage.removeItem(AuthService.AUTH_STATE_KEY);

    return valid;
  }

  isAuthRoute(route: string): boolean {
    const isAuthRoute = [
      'request-account-recovery',
      'reset-password',
      'request-new-account',
      'employee-login',
      'auth_redirect',
      'login',
    ].some((pattern) => route.includes(pattern));

    console.log({ route, isAuthRoute });

    return isAuthRoute;
  }
}
